import {
  APP_ID,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, delay, filter, of, switchMap, take, tap } from 'rxjs';
import { EntryActions } from '../state/entry.actions';
import {} from '../state/entry.reducer';
import { EntrySelectors } from '../state/entry.selectors';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { EntryService, MobileView } from './entry.service';
import { EntryData, ProviderData } from './structure-data.models';
import { SummaryData, TedDomain } from '../entry/structure-data.models';
import { CommonService } from '../common.service';
import { GoogleAnalyticsService } from '../google-analytics.service';
import { ConfigService } from '../config.service';
import { PlatformService } from '../platform.service';
import { MolstarPluginService } from '../molstar-plugin-service';
import { ScrollPositionService } from './scroll-position.service';
import { C } from '@angular/cdk/focus-monitor.d-CvvJeQRc';
import { ProviderCacheService } from './ProviderCacheService';
@Component({
  selector: 'app-entry',
  templateUrl: './entry.component.html',
  styleUrls: ['./entry.component.scss'],
  standalone: false,
})
export class EntryComponent implements OnInit {
  public readonly routeTabs = [
    { label: 'Summary and Model Confidence', id: 'summary' },
    { label: 'Domains', id: 'domains' },
    { label: 'Annotations', id: 'annotations' },
    { label: 'Similar Structure', id: 'structures' },
  ];
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private store = inject(Store);

  public currentMobileView = this.entryService.mobileView;
  public MobileView = MobileView;

  @ViewChild('tabs') tabGroup!: MatTabGroup;
  public selectedTab = signal<number>(0);
  private sub: any;
  pdbeMolstar: any;
  public summaryData: SummaryData;
  public afdb_accession: string;
  public isSummaryLoaded = false;
  public hasData: number;

  public isMobile = false;

  public csvAMFileUrl: string;
  public accession: string = '';
  public uniprotAccession: string = '';
  public entryData: EntryData;
  public isShowError = this.entryService.showError;
  public isEntryDataAvailable = false;
  public tedDomainData = toSignal(this.store.select(EntrySelectors.tedDomainData));
  public csvAmData = toSignal(this.store.select(EntrySelectors.csvAmData));
  public readonly scrollService = inject(ScrollPositionService);
  public providerData: ProviderData;
  constructor(
    public entryService: EntryService,
    private route: ActivatedRoute,
    public gaService: GoogleAnalyticsService,
    private platform: PlatformService,
    private breakpointObserver: BreakpointObserver,
    private configService: ConfigService,
    private commonService: CommonService,
    private providerCache: ProviderCacheService,
  ) {
    this.hasData = -1;
    this.route.queryParams.subscribe((params) => {
      const routeTabs = this.routeTabs;
      const tabName = params['activeTab'] ?? 'summary';
      const tabIndex = routeTabs.findIndex((tab) => tab.id === tabName);
      this.selectedTab.set(tabIndex);
      this.breakpointObserver
      .observe([`(max-width: 768px)`])
      .subscribe(result => {
        this.isMobile = result.matches;
      });
    });
  }
  neataccessionStore$ = this.store.select(EntrySelectors.accession);

  ngOnInit(): void {
    this.route.params
      .pipe(
        switchMap((params) => {
          const entryId = params['id'].toLowerCase();
          this.accession = entryId.toUpperCase();
  
          this.store.dispatch(EntryActions.setCurrentAccession({
            accession: this.accession,
          }));
          this.store.dispatch(EntryActions.getEntryData());
  
          // Wait until entryData has the field we care about
          return this.store.select(EntrySelectors.entryData).pipe(
            // adjust predicate to the field that proves data is "ready"
            filter((d): d is EntryData => !!d && typeof d.isUniProt === 'boolean'),
            take(1)
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data) => {
        // now `data` should be the populated entry object (not undefined)
        this.entryData = data;
        this.store.dispatch(EntryActions.setCurrentUniprotAccession({
          uniprotAccession: this.entryData.uniprotAccession
        }));
        this.store.dispatch(EntryActions.setCurrentIsUniprot({
          isUniProt: this.entryData.isUniProt
        }));
  
        this.isEntryDataAvailable = true;
        this.afdb_accession = this.entryData.modelEntityId;
        this.uniprotAccession = this.entryData.uniprotAccession;
        this.store.dispatch(EntryActions.getCSVData());
        this.store.dispatch(EntryActions.getTedDomainData());
  
        if (this.entryData.providerId) {
          this.getProviderData();
        }
  
        if (this.entryData.isUniProt) {
          this.store.dispatch(EntryActions.getUniprotData());
        }
      });
  }

  public getProviderData(): void {

    this.store.dispatch(EntryActions.getClustermmseqData());
    this.store.dispatch(EntryActions.getClusterfoldseekData());

    const providerId = this.entryData.providerId;
    // 1. Try cached value
    if(this.platform.isBrowserPlatform()){
      const cachedData = this.providerCache.get(providerId);
      if (cachedData) {
        this.providerData = cachedData;
        this.store.dispatch(
          EntryActions.setCurrentProvider({
            providerData: this.providerData
          })
        );
        return;
      }

      // 2. Otherwise, call API
      const auth = this.configService.getConfig().apiKey
        ? `?key=${this.configService.getConfig().apiKey}`
        : '';
      const apiUrl =
        this.configService.getConfig().providerApiUrl + providerId + auth;
      this.commonService.getApiData(apiUrl, { isInternal: true }).subscribe(
        (data) => {
          this.providerData = {
            providerName: data.providerName,
            toolUsed: this.entryData.toolUsed,
            providerUrl: data.providerUrl,
            feedback: data.feedback,
            license: data.license,
            citations: data.citations,
            copyrights: data.copyrights,
            modelCreatedDate: this.entryData.modelCreatedDate
          };
          this.store.dispatch(
            EntryActions.setCurrentProvider({
              providerData: this.providerData
            })
          );
          // Save in cache
          this.providerCache.set(providerId, this.providerData);
        },
        (error) => {
          this.providerData = {
            providerName : 'Google DeepMind',
            toolUsed: this.entryData.toolUsed,
            providerUrl: 'https://deepmind.com',
            feedback: 'alphafold@deepmind.com',
            license: 'CC-BY-4.0',
            citations: [{
              "description": "Jumper, J et al. Highly accurate protein structure prediction with AlphaFold. Nature (2021)",
              "url": "https://www.nature.com/articles/s41586-021-03819-2",
              "category": "AlphaFold"
            }],
            copyrights: [
              "AlphaFold Data Copyright (2022) DeepMind Technologies Limited.",
              "AlphaMissense Copyright (2023) DeepMind Technologies Limited."
            ],
            modelCreatedDate: this.entryData.modelCreatedDate
          };
          this.store.dispatch(
            EntryActions.setCurrentProvider({
              providerData: this.providerData
            })
          );
          this.providerCache.set(providerId, this.providerData);
        }
      );
    }
  }
  isObjectEmpty(obj: any): boolean {
    return obj && Object.keys(obj).length === 0;
  }

  async selectTab(event: MatTabChangeEvent) {
    const routeTabs = this.routeTabs;
    const tabName = routeTabs[event.index].id;
    this.router.navigate([], {
      queryParams: { activeTab: tabName },
      queryParamsHandling: 'merge',
    });
 
    if (this.platform.isBrowserPlatform()) {
      this.scrollService.handleScrollPosition(this.tabGroup, event.index);
    }
    //for GA tags
    let tag = "";
    let gtagmsg = "";
    if(tabName === "summary"){
      tag = "summary_model_confidence";
      gtagmsg = "Clicks on summary tab";
    }
    if(tabName === "domains"){
      tag = "domains_tab";
      gtagmsg = "Clicks on domains tab";
    }
    if(tabName === "annotations"){
      tag = "annotations_tab";
      gtagmsg = "Clicks on annotations tab";
    }
    if(tabName === "structures"){
      tag = "similar_proteins_tab";
      gtagmsg = "Clicks on similar proteins tab";
    }
    this.gaService.eventEmitter('tab_navigation', 'navigation', 'click', tag, gtagmsg);
  }
}
